import { Route, Routes, Navigate } from 'react-router-dom';
import { LoginPage } from './pages/Login.js';
import { ProfilePage } from './pages/Profile.js';
import { SettingsConnectionsPage } from './pages/SettingsConnections.js';
import { DocumentsPage } from './pages/Documents.js';
import { VideosPage } from './pages/Videos.js';
import { VideoLibraryPage } from './pages/VideoLibraryPage.js';
import { VideoDetailPage } from './pages/VideoDetailPage.js';
import { AdminKnowledgebasePage } from './pages/AdminKnowledgebase.js';
import { AdminConfluencePage } from './pages/AdminConfluence.js';
import { AdminJiraSourcesPage } from './pages/AdminJiraSources.js';
import { AdminCodeSourcesPage } from './pages/AdminCodeSources.js';
import { AdminCanonicalFactsPage } from './pages/AdminCanonicalFacts.js';
import { AdminSpecialisationsPage } from './pages/AdminSpecialisations.js';
import { AdminDocumentTypesPage } from './pages/AdminDocumentTypes.js';
import { AdminDocTemplatesPage } from './pages/AdminDocTemplates.js';
import { HelpPage } from './pages/Help.js';
import { AdminExtractorTemplatesPage } from './pages/AdminExtractorTemplates.js';
import { AdminTemplateBuilderPage } from './pages/AdminTemplateBuilder.js';
import { AdminApiKeysPage } from './pages/AdminApiKeys.js';
import { ExtractorPage } from './pages/Extractor.js';
import { ExtractorRunPage } from './pages/ExtractorRun.js';
import { KnowledgeAgentPage } from './pages/KnowledgeAgent.js';
import { MyAgentPage } from './pages/MyAgent.js';
import { AuthProvider } from './auth/AuthProvider.js';
import { RequireAuth } from './auth/RequireAuth.js';
import { RoleGuard } from './auth/RoleGuard.js';
import { NewVersionToast } from './components/NewVersionToast.js';
import type { UserRole } from '@kennisbank/shared';

// Every role may chat. `/` redirects to /agent, so a role missing here
// loops /agent → / → /agent. Mirrors ALLOWED_ROLES in the backend
// agent/chats/my-agent routes.
const CHAT_ROLES: UserRole[] = [
  'kb_admin',
  'kb_user',
  'exco_user',
  'sensitive_user',
  'confidential_user',
];

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <NewVersionToast />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/*
          Default landing post-login → /agent. Single Navigate-based
          redirect keeps the existing RoleGuard on /agent as the
          single enforcement point for who-can-chat.

          Legacy paths (/kennisagent, /documenten, /beheer/kennisbank) are
          aliased so any bookmarks or stale links from the SUPPOHRT origin
          continue to redirect rather than 404.
        */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <Navigate to="/agent" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/documents"
          element={
            <RequireAuth>
              <DocumentsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/videos"
          element={
            <RequireAuth>
              <VideoLibraryPage />
            </RequireAuth>
          }
        />
        <Route
          path="/videos/:id"
          element={
            <RequireAuth>
              <VideoDetailPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/videos"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <VideosPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/agent"
          element={
            <RequireAuth>
              <RoleGuard allowed={CHAT_ROLES}>
                <KnowledgeAgentPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/my-agent"
          element={
            <RequireAuth>
              <RoleGuard allowed={CHAT_ROLES}>
                <MyAgentPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/documents"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminKnowledgebasePage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/confluence"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminConfluencePage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/jira-sources"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminJiraSourcesPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/code-sources"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminCodeSourcesPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/canonical-facts"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminCanonicalFactsPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/specialisations"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminSpecialisationsPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/document-types"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminDocumentTypesPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/doc-templates"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminDocTemplatesPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        <Route
          path="/help"
          element={
            <RequireAuth>
              <HelpPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/extractor-templates"
          element={
            <RequireAuth>
              <AdminExtractorTemplatesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/template-builder"
          element={
            <RequireAuth>
              <AdminTemplateBuilderPage />
            </RequireAuth>
          }
        />
        <Route
          path="/workspace/extractor"
          element={
            <RequireAuth>
              <ExtractorPage />
            </RequireAuth>
          }
        />
        <Route
          path="/workspace/extractor/runs/:id"
          element={
            <RequireAuth>
              <ExtractorRunPage />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/api-keys"
          element={
            <RequireAuth>
              <RoleGuard allowed={['kb_admin']}>
                <AdminApiKeysPage />
              </RoleGuard>
            </RequireAuth>
          }
        />
        {/* Legacy redirects */}
        <Route path="/kennisagent" element={<Navigate to="/agent" replace />} />
        <Route path="/documenten" element={<Navigate to="/documents" replace />} />
        <Route path="/beheer/kennisbank" element={<Navigate to="/admin/documents" replace />} />
        <Route
          path="/profile"
          element={
            <RequireAuth>
              <ProfilePage />
            </RequireAuth>
          }
        />
        <Route
          path="/settings/connections"
          element={
            <RequireAuth>
              <SettingsConnectionsPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
